// The sensors row — pinned directly under the common-devices strip.
//
// WHY IT IS ITS OWN ROW, ABOVE THE GRID. A sensor is not a control, and the
// device grid is a grid of controls. Left among them, a door sensor is a card
// that looks tappable, opens a screen with nothing on it, and buries the one
// thing it does have to say — its state — behind a tap. Up here the state is
// the card, and it is visible without touching anything.
//
// Same two-container rule as CommonDevicesStrip: this owns a horizontal scroll,
// the grid below owns the vertical one, and `flexShrink: 0` stops a long device
// list from squeezing the row to nothing.
//
// Renders NOTHING when there are no sensors — unlike the common strip, which
// says "No Common Devices Found" because its absence is worth reporting. Most
// rooms have no sensor, and an empty band in every one of them is noise.

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import SensorCard from './SensorCard';
import RfSensorTile from '../../shared/RfSensorTile';
// Same card size as the common strip — see the note there. Two stacked rows of
// the same kind of thing must not be two different sizes.
import { STRIP_CARD_SIZE } from './CommonDevicesStrip';

// ⚠️ TWO KINDS OF SENSOR SHARE THIS ROW, and only one of them is a device.
// `devices` are DevicesInstalled rows with a unique number; `rfSensors` are
// RfDevice rows — a door, safe or fire transmitter that has no unique number at
// all and therefore appears in no room query (docs/17). They read the same to
// the person looking at them, so they are drawn in one row; they are fetched
// and refreshed by different code, so they arrive as two lists.
export default function SensorStrip({
  devices, rfSensors = [], onOpen, onOpenRf, onToggleFavourite, hiddenCount = 0,
}) {
  const theme = useTheme();
  const c = theme.sli;

  // Nothing to draw AND nothing withheld — say nothing at all.
  if ((!devices || devices.length === 0) && rfSensors.length === 0 && hiddenCount === 0) return null;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
  const cardSize = STRIP_CARD_SIZE(vw);

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, px: 2, pt: 0.5 }}>
        <Typography
          sx={{
            color: c.text, opacity: 0.75, fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
          }}
        >
          SENSORS
        </Typography>
        {hiddenCount > 0 && (
          <Typography sx={{ color: c.text, opacity: 0.6, fontSize: 11 }}>
            {`· ${hiddenCount} offline hidden`}
          </Typography>
        )}
      </Box>
      {/* data-hscroll: a real horizontal scroller, so the tab-swipe handler
          leaves gestures that start in here alone (see useSwipeNav). */}
      <Box
        data-hscroll
        sx={{
          display: 'flex', overflowX: 'auto', px: 1, py: 1, gap: 2,
        }}
      >
        {(devices || []).map((d) => (
          <Box key={d.UniqueNumber} sx={{ width: cardSize, height: cardSize, flexShrink: 0 }}>
            <SensorCard
              device={d}
              onOpen={onOpen}
              onToggleFavourite={onToggleFavourite}
            />
          </Box>
        ))}
        {rfSensors.map((d) => (
          <Box key={`rf-${d.RfDeviceId}`} sx={{ width: cardSize, height: cardSize, flexShrink: 0 }}>
            <RfSensorTile device={d} onOpen={onOpenRf} compact />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
